import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { getYear, getMonth, getDate, parseISO } from 'date-fns';
import * as Yup from 'yup';
import 'yup-phone';

import { abbreviatedStatesArray } from '@evdy/web-etc/lib/repZones';

import StepContainer from '../StepContainer';
import FixedFooter from '../Footer/FixedFooter';
import Evelyn from '../Evelyn';
import PackageName from '../PackageName';
import TextInput from '../TextInput';
import DropdownInput from '../DropdownInput';
import DateInput from '../DateInput';
import RadioBtnInput from '../RadioBtnInput';
import EditBirthdayModal from '../Modals/EditBirthdayModal';
import RadioBtnCard from '../RadioBtnCard';

import { fetchClientConfig } from '@evdy/web-redux/dist/actions/fetchClientConfig';
import { authUserOperations } from '@evdy/web-redux/dist/lib/authUser';

import { formatEven, formatPhoneNumber, getSelectedAddOnsPriceTotals } from '../../helperFns';

import styles from './PurchaseSummary.module.scss';

import awardRibbon from '../../public/images/icons/award-ribbon-icon-everdays-blue.svg';
import chevRight from '../../public/images/icons/chevron-right-everdays-blue.svg';

const { saveLastScreen, submitOrder, resetUserBirthdayUpdated } = authUserOperations;

const {
  'purchase-summary': purchaseSummaryClass,
  'order-summary': orderSummaryClass,
  'package-and-provider': packageAndProviderClass,
  'provider-name': providerNameClass,
  'owed-today': owedTodayClass,
  price: priceClass,
  'order-info': orderInfoClass,
  'info-section': infoSectionClass,
  'info-points': infoPointsClass,
  'dotted-line': dottedLineClass,
  'info-point': infoPointClass,
  'point-number': pointNumberClass,
  'point-copy': pointCopyClass,
  'payment-options': paymentOptionsClass,
  'credit-cards': creditCardsClass,
  'custom-evelyn': customEvelynClass,
  disclaimer: disclaimerClass,
  // form styles
  'form-container': formContainerClass,
  'select-plan-section': selectPlanSectionClass,
  'plan-card': planCardClass,
  'plan-error': planErrorClass,
  'your-info': yourInfoClass,
  'form-section-title': formSectionTitleClass,
  'edit-btn-wrapper': editBtnWrapperClass,
  'radio-group': radioGroupClass,
  'radio-title': radioTitleClass,
  error: errorClass,
  'error-text': errorTextClass,
  'gender-input': genderInputClass,
  inputs: inputsClass,
  'custom-date-input': customDateInputClass,
  'custom-dropdown': customDropdownClass,
  medicaid: medicaidClass
} = styles;

const infoPoints = isPreferred => [
  {
    title: "You'll get a confirmation email",
    subtitle: ''
  },
  {
    title: `We'll finalize your ${isPreferred ? 'order' : 'pricing'}`,
    subtitle: ''
  },
  {
    title: 'Finish payment online in minutes',
    subtitle: ''
  }
];

const PurchaseSummary = ({ location }) => {
  const { authUser, clientConfig } = useSelector(({ authUser, client }) => ({
    authUser: authUser,
    clientConfig: client?.config?.data
  }));

  const dispatch = useDispatch();
  const phoneRef = useRef(null);
  const [modalOpen, setModalOpen] = useState('');

  const { data: userData, orderSubmitted, isFetching, birthdayUpdated } = authUser || {};
  const {
    userInfo,
    packageId: currentPackageId,
    medicaid,
    funeralHome,
    estimates,
    addOns = [],
    paymentOptions: preneedPaymentOptions = []
  } = userData?.preneed || {};
  const { name, gender, dateOfBirth, address, phone } = userInfo ?? {};
  const { name: funeralHomeName, packageSetupComplete } = funeralHome || {};

  const parsedDOB = dateOfBirth && parseISO(dateOfBirth);
  const initialDOBValues = parsedDOB
    ? {
        birthMonth: getMonth(parsedDOB) + 1, // month is zero-indexed, so add 1 to match standard month numbers,
        birthDay: getDate(parsedDOB),
        birthYear: getYear(parsedDOB)
      }
    : { birthMonth: '', birthDay: '', birthYear: '' };

  const currentUserPackage = preneedPaymentOptions?.find(pkg => currentPackageId === pkg.packageId);
  const { packageName, outright, insurance } = currentUserPackage || {};
  const { premium: outrightPrice } = outright ?? {};
  const { premium: insurancePrice, years: insuranceYears } = insurance ?? {};

  // these are used if price totals are calculated on client-side/FE
  // const selectedAddOnsPriceTotals = getSelectedAddOnsPriceTotals(addOns);
  // const calculatedOutrightPrice = outrightPrice + selectedAddOnsPriceTotals.outright;
  // const calculatedMonthlyPrice = insurancePrice + selectedAddOnsPriceTotals.monthly;

  // these payment options refer to the credit cards that come in via client config,
  // they are not related to the user's preneed package
  const { paymentOptions } = clientConfig?.preneed || {};

  const isPreferred = packageSetupComplete;

  const initialValues = {
    // select plan
    plan: userData?.preneed?.plan?.planType || 'outright',
    // user info form
    firstName: name?.first || '',
    lastName: name?.last || '',
    gender: gender || '',
    ...initialDOBValues,
    street1: address?.street1 || '',
    city: address?.city || '',
    state: address?.state || '',
    zip: address?.zip || '',
    phone: phone ? formatPhoneNumber({ phoneNumberString: phone, phoneRef }) : '',
    medicaid: medicaid ?? false
  };

  const validationSchema = Yup.object().shape({
    plan: Yup.string().required(),
    firstName: Yup.string().required('First Name is required'),
    lastName: Yup.string().required('Last Name is required'),
    gender: Yup.string().required('Gender is required'),
    birthMonth: Yup.number()
      .typeError('Enter valid number')
      .min(1, 'Enter valid month')
      .max(12, 'Enter valid month')
      .required('Month is required!'),
    birthDay: Yup.number()
      .typeError('Enter valid number')
      .min(1, 'Enter a valid day')
      .max(31, 'Enter a valid day')
      .required('Day is required!'),
    birthYear: Yup.number()
      .typeError('Enter valid number')
      .min(1880, 'Enter a valid year')
      .max(new Date().getFullYear(), 'Enter a valid year')
      .required('Year is required!'),
    street1: Yup.string().required('Street address is required'),
    city: Yup.string().required('City is required'),
    state: Yup.string().required('State is required'),
    zip: Yup.string()
      .required()
      .matches(/^[0-9]+$/, 'Enter a valid zipcode')
      .min(5, 'Enter a valid zipcode')
      .max(5, 'Enter a valid zipcode'),
    phone: Yup.string()
      .phone('US', true, 'Please enter a valid phone number.')
      .required('Phone is required!'),
    medicaid: Yup.bool().required('Select Medicaid Eligibility')
  });

  const birthDateInputProps = {
    label: 'Birthday',
    monthName: 'birthMonth',
    monthPlaceholder: 'MM',
    dayName: 'birthDay',
    dayPlaceholder: 'DD',
    yearName: 'birthYear',
    yearPlaceholder: 'YYYY'
  };

  // on mount, fetch client config
  useEffect(() => {
    dispatch(fetchClientConfig());
  }, []);

  // after order submitted, save last screen and navigate to thank you page
  useEffect(() => {
    if (orderSubmitted) {
      dispatch(saveLastScreen({ screen: 'orderCompletionSummary' }));
    }
  }, [orderSubmitted, dispatch]);

  const handleCloseModal = () => setModalOpen('');

  // close birthdate modal on success
  useEffect(() => {
    if (birthdayUpdated) {
      handleCloseModal();
      dispatch(resetUserBirthdayUpdated());
    }
  }, [birthdayUpdated, dispatch]);

  const onSubmit = values => {
    const strippedPhone = values?.phone?.toString()?.replace(/\D+/g, '');

    const submitValues = {
      ...values,
      phone: strippedPhone
    };

    dispatch(submitOrder({ userInfo, values: submitValues }));
  };

  const evelynArray = [
    'Once we process your paperwork we’ll confirm your exact price, finalize your order, and collect payment to complete your purchase.',
    { content: "Let's start your order.", style: 'header' }
  ];

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      validateOnMount
    >
      {({ errors, touched, values, handleSubmit, isValid }) => {
        const genderInputError = errors.gender && touched.gender;
        const genderRadioClasses = classNames(radioGroupClass, genderInputClass, {
          [errorClass]: !!genderInputError
        });
        return (
          <>
            <StepContainer evelynArray={evelynArray} showLoader={isFetching} {...{ location }}>
              <div className={purchaseSummaryClass}>
                <div className={orderSummaryClass}>
                  <div className={packageAndProviderClass}>
                    <PackageName name={packageName} decedentName={`${name?.first} ${name?.last}`} />
                    {isPreferred ? (
                      <div className={providerNameClass}>
                        <img src={awardRibbon} alt="" />
                        <p>{funeralHomeName} funeral home</p>
                      </div>
                    ) : (
                      ''
                    )}
                  </div>
                  <div className={owedTodayClass}>
                    <p>Due today</p>
                    <p className={priceClass}>$0</p>
                  </div>
                  <div className={paymentOptionsClass}>
                    <span>{paymentOptions?.title}</span>
                    <div className={creditCardsClass}>
                      {paymentOptions?.imageUrls?.map((card, idx) => (
                        <img src={card} key={idx} alt="" />
                      ))}
                    </div>
                  </div>
                </div>
                <div className={orderInfoClass}>
                  <div className={infoSectionClass}>
                    <h3>Curious what happens next?</h3>
                    <div className={infoPointsClass}>
                      <div className={dottedLineClass} />
                      {infoPoints(isPreferred).map((point, idx) => (
                        <div key={point.title} className={infoPointClass}>
                          <div className={pointNumberClass}>{idx + 1}</div>
                          <div className={pointCopyClass}>
                            <h4>{point.title}</h4>
                            <p>{point.subtitle}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Evelyn
                    alignBubbles
                    customClass={customEvelynClass}
                    speechBubbleArray={[
                      <span>
                        Remember, locking in your funeral price today saves you, your family, and
                        your estate <strong>40% or more!*</strong>
                      </span>
                    ]}
                  />
                </div>
                <Form>
                  <div className={`${formContainerClass} ${yourInfoClass}`}>
                    <h4 className={formSectionTitleClass}>{`How will you pay?${
                      !isPreferred ? '†' : ''
                    }`}</h4>
                    <div className={selectPlanSectionClass}>
                      <RadioBtnCard
                        customClass={planCardClass}
                        fieldName="plan"
                        value="outright"
                        isChecked={values?.plan === 'outright'}
                      >
                        <div>
                          <p>Pay in Full</p>
                          <strong>{formatEven(outrightPrice)}</strong>
                        </div>
                      </RadioBtnCard>
                      <RadioBtnCard
                        customClass={planCardClass}
                        fieldName="plan"
                        value="insurance"
                        isChecked={values?.plan === 'insurance'}
                      >
                        <div>
                          <p>Pay Over Time</p>
                          <strong>{formatEven(insurancePrice)}/mo</strong>
                          <span>&nbsp;({insuranceYears * 12} mos)</span>
                        </div>
                      </RadioBtnCard>
                    </div>
                    {errors.plan && touched.plan && (
                      <p className={planErrorClass}>Please select a plan.</p>
                    )}
                    <h4 className={formSectionTitleClass}>Your information</h4>
                    <div className={`${inputsClass} half`}>
                      <TextInput type="text" label="First Name" name="firstName" />
                      <TextInput type="text" label="Last Name" name="lastName" />
                    </div>
                    <div className={`${inputsClass} half`}>
                      <div className={genderRadioClasses}>
                        <div className={radioTitleClass}>Gender</div>
                        <div>
                          <RadioBtnInput
                            fieldName="gender"
                            title="Male"
                            value="Male"
                            isChecked={values?.gender === 'Male'}
                          />
                          <RadioBtnInput
                            fieldName="gender"
                            title="Female"
                            value="Female"
                            isChecked={values?.gender === 'Female'}
                          />
                        </div>
                        <div className={errorTextClass}>
                          {errors.gender && touched.gender && errors.gender}
                        </div>
                      </div>
                      <div className={editBtnWrapperClass}>
                        <button type="button" onClick={() => setModalOpen('birthdayModal')}>
                          Edit <img src={chevRight} alt="right chev" />
                        </button>
                        <DateInput
                          customInputClass={customDateInputClass}
                          footnote="Changing your birthday may affect insurance pricing"
                          locked
                          {...birthDateInputProps}
                        />
                      </div>
                    </div>
                    <div className={`${inputsClass} full`}>
                      <TextInput
                        type="tel"
                        label="Phone"
                        name="phone"
                        placeholder="800-555-1234"
                        onInput={e =>
                          formatPhoneNumber({ phoneNumberString: e?.target?.value, phoneRef })
                        }
                        ref={phoneRef}
                      />
                    </div>
                    <div className={`${inputsClass} full`}>
                      <TextInput name="street1" label="Address" placeholder="Your home address" />
                    </div>
                    <div className={`${inputsClass} custom`}>
                      <TextInput
                        customClass="large"
                        name="city"
                        label="City"
                        placeholder="City name"
                      />
                      <DropdownInput
                        customClass={`dropdown ${customDropdownClass}`}
                        label="State"
                        name="state"
                        type="text"
                        placeholder="Select a State"
                        options={abbreviatedStatesArray}
                      />
                      <TextInput
                        customClass="small"
                        name="zip"
                        label="Zip code"
                        placeholder="#####"
                      />
                    </div>
                    {values?.plan === 'outright' ? (
                      <div className={medicaidClass}>
                        <h4>Are you trying to qualify for Medicaid?</h4>
                        <p>
                          If so, select yes to make this purchase irrevocable so these funds are
                          excluded from your net assets for Medicaid eligibility purposes.
                        </p>
                        <div className={`${inputsClass} half`}>
                          <div className={radioGroupClass}>
                            <div>
                              <RadioBtnInput
                                fieldName="medicaid"
                                title="Yes"
                                value={true}
                                isChecked={values?.medicaid === true}
                              />
                              <RadioBtnInput
                                fieldName="medicaid"
                                title="No"
                                value={false}
                                isChecked={values?.medicaid === false}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      ''
                    )}
                  </div>
                </Form>
                <p className={disclaimerClass}>
                  *Based on historical inflation rates and typical life expectancy.
                </p>
                {!isPreferred ? (
                  <p className={disclaimerClass}>
                    †These prices are estimates and final pricing will be confirmed once we process
                    your order.
                  </p>
                ) : (
                  ''
                )}
              </div>
            </StepContainer>
            <FixedFooter
              {...{ isValid }}
              backCopy="Back"
              backAction={() => {
                dispatch(saveLastScreen({ screen: 'completeDetails' }));
              }}
              nextCopy="Submit Order"
              nextAction={handleSubmit}
            />
            <EditBirthdayModal
              closeModal={handleCloseModal}
              modalVisible={modalOpen === 'birthdayModal'}
              {...{ userData }}
            />
          </>
        );
      }}
    </Formik>
  );
};

export default PurchaseSummary;
